/**
 * Phone number validation and formatting utilities
 * Supports E.164 format required by Supabase
 */

// Common country codes for phone number formatting
export const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸', name: 'United States / Canada' },
  { code: '+44', country: 'UK', flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+61', country: 'AU', flag: '🇦🇺', name: 'Australia' },
  { code: '+33', country: 'FR', flag: '🇫🇷', name: 'France' },
  { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Germany' },
  { code: '+81', country: 'JP', flag: '🇯🇵', name: 'Japan' },
  { code: '+86', country: 'CN', flag: '🇨🇳', name: 'China' },
  { code: '+91', country: 'IN', flag: '🇮🇳', name: 'India' },
  { code: '+55', country: 'BR', flag: '🇧🇷', name: 'Brazil' },
  { code: '+7', country: 'RU', flag: '🇷🇺', name: 'Russia' },
];

/**
 * Validates if a phone number is in E.164 format
 * E.164 format: +[country code][national number]
 * Example: +1234567890
 */
export const isValidE164 = (phone: string): boolean => {
  if (!phone) return false;
  
  // E.164 format: + followed by 1-15 digits
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  return e164Regex.test(phone);
};

/**
 * Formats a phone number to E.164 format
 * Attempts to auto-detect country code or uses provided default
 */
export const formatToE164 = (phone: string, defaultCountryCode: string = '+1'): string => {
  if (!phone) return '';
  
  // Remove all non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // If already starts with +, validate and return
  if (cleaned.startsWith('+')) {
    return isValidE164(cleaned) ? cleaned : '';
  }
  
  // If starts with country code without +, add it
  if (cleaned.length > 10) {
    const withPlus = '+' + cleaned;
    if (isValidE164(withPlus)) {
      return withPlus;
    }
  }
  
  // If it's a national number, add default country code
  if (cleaned.length >= 7 && cleaned.length <= 15) {
    const withCountryCode = defaultCountryCode + cleaned;
    return isValidE164(withCountryCode) ? withCountryCode : '';
  }
  
  return '';
};

/**
 * Formats phone number for display (human-readable)
 * Converts E.164 to a more readable format
 */
export const formatPhoneForDisplay = (phone: string): string => {
  if (!phone || !isValidE164(phone)) return phone;
  
  // US/Canada format: +1 (555) 123-4567
  if (phone.startsWith('+1') && phone.length === 12) {
    const number = phone.slice(2);
    return `+1 (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
  }
  
  // UK format: +44 20 1234 5678
  if (phone.startsWith('+44')) {
    const number = phone.slice(3);
    if (number.length === 10) {
      return `+44 ${number.slice(0, 2)} ${number.slice(2, 6)} ${number.slice(6)}`;
    }
  }
  
  // Generic format: +XX XXX XXX XXXX
  if (phone.length > 8) {
    const countryCode = phone.slice(0, phone.length - 10);
    const number = phone.slice(phone.length - 10);
    return `${countryCode} ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
  }
  
  return phone;
};

/**
 * Validates phone number and returns validation result
 */
export interface PhoneValidationResult {
  isValid: boolean;
  formatted: string;
  error?: string;
}

export const validatePhone = (phone: string, countryCode: string = '+1'): PhoneValidationResult => {
  if (!phone.trim()) {
    return {
      isValid: false,
      formatted: '',
      error: 'Phone number is required'
    };
  }
  
  const formatted = formatToE164(phone, countryCode);
  
  if (!formatted) {
    return {
      isValid: false,
      formatted: phone,
      error: 'Please enter a valid phone number'
    };
  }
  
  if (!isValidE164(formatted)) {
    return {
      isValid: false,
      formatted: phone,
      error: 'Phone number must be in international format (e.g., +1234567890)'
    };
  }
  
  return {
    isValid: true,
    formatted,
    error: undefined
  };
};

/**
 * Detects country code from phone number
 */
export const detectCountryCode = (phone: string): string => {
  if (!phone.startsWith('+')) return '+1';
  
  // Find matching country code
  for (const country of COUNTRY_CODES) {
    if (phone.startsWith(country.code)) {
      return country.code;
    }
  }
  
  return '+1';
};

/**
 * Gets country info from country code
 */
export const getCountryInfo = (countryCode: string) => {
  return COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0];
};
